import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Camera, Check, ChevronLeft, ChevronRight, AlertTriangle, Save,
  FileText, MapPin, Pen, X, Star, Calendar, Upload, Wifi, WifiOff,
  CheckCircle2, XCircle, Minus, Clock, Eye, ArrowRight, RotateCcw,
  AlertCircle, Info, StickyNote, ImagePlus, Hash
} from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { mockVessels, mockQuestionnaire } from '../../data/mockData';
import { useOfflineStatus } from '../../hooks/useOfflineStatus';
import { offlineDB } from '../../utils/offlineStorage';
import type { Question, QuestionnaireSection, AnswerResult, Severity, RiskLevel } from '../../types';

interface Answer {
  questionId: string;
  result: AnswerResult;
  value?: string;
  notes: string;
  photos: string[];
  gpsLocation?: { lat: number; lng: number };
  severity?: Severity;
  riskLevel?: RiskLevel;
  finding?: string;
  correctiveAction?: string;
  responsible?: string;
}

interface FormState {
  answers: Record<string, Answer>;
  inspectorNotes: string;
  signatureData: string | null;
}

export default function ConductInspectionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vesselId = searchParams.get('vessel') || 'vsl-001';
  const { currentUser } = useAppStore();
  const { isOnline, pendingSync } = useOfflineStatus();

  const vessel = mockVessels.find(v => v.id === vesselId) || mockVessels[0];
  const questionnaire = mockQuestionnaire;
  const sections = questionnaire.sections;

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0); // 0 = setup, 1..N = sections, N+1 = review
  const [formState, setFormState] = useState<FormState>({
    answers: {},
    inspectorNotes: '',
    signatureData: null,
  });
  const [inspectionType, setInspectionType] = useState<string>('periodic');
  const [isSaving, setIsSaving] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [activeQuestionForPhoto, setActiveQuestionForPhoto] = useState<string | null>(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [showNotesFor, setShowNotesFor] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const totalSteps = sections.length + 2; // setup + sections + review
  const progress = currentStep === 0 ? 0 : currentStep >= totalSteps - 1 ? 100 : Math.round((currentStep / (totalSteps - 1)) * 100);

  // Get section completion
  const getSectionProgress = useCallback((section: QuestionnaireSection) => {
    const answered = section.questions.filter(q => formState.answers[q.id]?.result && formState.answers[q.id]?.result !== 'pending').length;
    return { answered, total: section.questions.length, pct: Math.round((answered / section.questions.length) * 100) };
  }, [formState.answers]);

  // Set answer
  const setAnswer = (questionId: string, result: AnswerResult, notes?: string) => {
    setFormState(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: {
          questionId,
          result,
          value: prev.answers[questionId]?.value,
          notes: notes ?? prev.answers[questionId]?.notes ?? '',
          photos: prev.answers[questionId]?.photos ?? [],
          gpsLocation: prev.answers[questionId]?.gpsLocation,
          severity: prev.answers[questionId]?.severity,
          riskLevel: prev.answers[questionId]?.riskLevel,
          finding: prev.answers[questionId]?.finding ?? '',
          correctiveAction: prev.answers[questionId]?.correctiveAction ?? '',
          responsible: prev.answers[questionId]?.responsible ?? '',
        }
      }
    }));
  };

  // Set observation fields
  const setObservation = (questionId: string, field: string, value: any) => {
    setFormState(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: {
          ...prev.answers[questionId],
          questionId,
          result: prev.answers[questionId]?.result ?? 'pending',
          notes: prev.answers[questionId]?.notes ?? '',
          photos: prev.answers[questionId]?.photos ?? [],
          finding: prev.answers[questionId]?.finding ?? '',
          correctiveAction: prev.answers[questionId]?.correctiveAction ?? '',
          responsible: prev.answers[questionId]?.responsible ?? '',
          [field]: value,
        }
      }
    }));
  };

  // Auto-save
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (currentStep > 0) {
        setSaveStatus('saving');
        try {
          await offlineDB.saveInspection({
            id: `ins-draft-${vesselId}`,
            vesselId,
            answers: formState.answers,
            notes: formState.inspectorNotes,
            signature: formState.signatureData,
            progress,
          });
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
        } catch {
          setSaveStatus('error');
        }
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [formState, currentStep]);

  // Load saved draft
  useEffect(() => {
    (async () => {
      try {
        const saved = await offlineDB.getInspection(`ins-draft-${vesselId}`) as any;
        if (saved?.answers) {
          setFormState({
            answers: saved.answers,
            inspectorNotes: saved.notes || '',
            signatureData: saved.signature,
          });
        }
      } catch {}
    })();
  }, [vesselId]);

  // Summary stats
  const allAnswers = Object.values(formState.answers);
  const passCount = allAnswers.filter(a => a.result === 'pass').length;
  const failCount = allAnswers.filter(a => a.result === 'fail').length;
  const naCount = allAnswers.filter(a => a.result === 'na').length;
  const totalAnswered = passCount + failCount + naCount;
  const totalQuestions = sections.reduce((sum, s) => sum + s.questions.length, 0);

  const navigation = (
    <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
      <button
        onClick={() => currentStep > 0 && setCurrentStep(s => s - 1)}
        disabled={currentStep === 0}
        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
      >
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <div className="flex items-center gap-2">
        {saveStatus === 'saving' && (
          <span className="flex items-center gap-1.5 text-xs text-slate-400">
            <div className="w-3 h-3 border-2 border-slate-300 border-t-sky-500 rounded-full animate-spin" /> Saving...
          </span>
        )}
        {saveStatus === 'saved' && (
          <span className="flex items-center gap-1.5 text-xs text-emerald-500">
            <Check className="w-3 h-3" /> Saved
          </span>
        )}
        {currentStep < totalSteps - 1 ? (
          <button
            onClick={() => setCurrentStep(s => s + 1)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-sky-500 text-white hover:bg-sky-600 shadow-md shadow-sky-500/20 transition-all"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={() => navigate('/app/inspections')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 shadow-md shadow-emerald-500/20 transition-all"
          >
            <CheckCircle2 className="w-4 h-4" /> Submit Inspection
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <button onClick={() => navigate('/app/inspections')} className="text-sm text-slate-400 hover:text-sky-600 transition-colors flex items-center gap-1 mb-1">
            <ChevronLeft className="w-3.5 h-3.5" /> Back to Inspections
          </button>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Conduct Inspection</h1>
          <p className="text-sm text-slate-500 mt-1">
            {vessel.name} · {questionnaire.name} v{questionnaire.version}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Online/Offline indicator */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${isOnline ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
            {isOnline ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            {isOnline ? 'Online' : 'Offline'}
            {!isOnline && pendingSync > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded-full text-[10px] font-bold">{pendingSync}</span>
            )}
          </div>
          <div className="px-3 py-1.5 bg-slate-100 rounded-full text-xs font-medium text-slate-600">
            <Clock className="w-3.5 h-3.5 inline mr-1" />
            {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-600">
            Step {currentStep === 0 ? 'Setup' : currentStep >= totalSteps - 1 ? 'Review' : `${currentStep} of ${sections.length}`}
          </span>
          <span className="text-sm font-bold text-sky-600">{progress}%</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-sky-400 to-sky-600 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        {/* Section indicators */}
        <div className="flex gap-1.5 mt-3 overflow-x-auto pb-1">
          {/* Setup dot */}
          <button
            onClick={() => setCurrentStep(0)}
            className={`w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold flex-shrink-0 transition-all ${
              currentStep === 0 ? 'bg-sky-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            S
          </button>
          {sections.map((section, i) => {
            const prog = getSectionProgress(section);
            const isComplete = prog.pct === 100;
            const isActive = currentStep === i + 1;
            return (
              <button
                key={section.id}
                onClick={() => setCurrentStep(i + 1)}
                className={`w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold flex-shrink-0 transition-all ${
                  isActive ? 'bg-sky-500 text-white ring-2 ring-sky-200' :
                  isComplete ? 'bg-emerald-500 text-white' :
                  prog.answered > 0 ? 'bg-amber-400 text-white' :
                  'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
                title={section.title}
              >
                {isComplete ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </button>
            );
          })}
          <button
            onClick={() => setCurrentStep(totalSteps - 1)}
            className={`w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold flex-shrink-0 transition-all ${
              currentStep === totalSteps - 1 ? 'bg-sky-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* STEP 0: Setup */}
      {currentStep === 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Inspection Setup</h2>
            <p className="text-sm text-slate-500 mt-1">Configure the inspection before starting</p>
          </div>

          {/* Vessel Info Card */}
          <div className="p-4 bg-gradient-to-r from-[#0A1628] to-[#1E3A5F] rounded-2xl text-white">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-sky-300" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">{vessel.name}</h3>
                <p className="text-sm text-slate-300">{vessel.imoNumber} · {vessel.type} · {vessel.flag}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                  <span>DWT: {vessel.deadweight.toLocaleString()}</span>
                  <span>•</span>
                  <span>Manager: {vessel.manager}</span>
                  <span>•</span>
                  <span>Score: {vessel.lastRightShipScore}/10</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">Inspection Type</label>
              <select
                value={inspectionType}
                onChange={e => setInspectionType(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <option value="initial">Initial Inspection</option>
                <option value="periodic">Periodic Inspection</option>
                <option value="follow_up">Follow-up Inspection</option>
                <option value="unannounced">Unannounced Inspection</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">Inspector</label>
              <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700">
                {currentUser?.name} ({currentUser?.role})
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">Scheduled Date</label>
              <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700">
                {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">Questionnaire</label>
              <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700">
                {questionnaire.name} (v{questionnaire.version})
              </div>
            </div>
          </div>

          <div className="p-4 bg-sky-50 border border-sky-100 rounded-xl flex items-start gap-3">
            <Info className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-sky-800">Offline Mode Available</p>
              <p className="text-xs text-sky-600 mt-0.5">You can complete this inspection offline. All data will be saved locally and synced when connectivity is restored.</p>
            </div>
          </div>

          {navigation}
        </div>
      )}

      {/* STEPS 1-N: Section Questions */}
      {currentStep > 0 && currentStep <= sections.length && (
        <SectionStep
          section={sections[currentStep - 1]}
          answers={formState.answers}
          sectionIndex={currentStep - 1}
          totalSections={sections.length}
          onSetAnswer={setAnswer}
          onSetObservation={setObservation}
          onOpenNotes={setShowNotesFor}
          onOpenPhoto={(qId) => { setActiveQuestionForPhoto(qId); setShowPhotoModal(true); }}
          notes={formState.inspectorNotes}
          onNotesChange={(v) => setFormState(s => ({ ...s, inspectorNotes: v }))}
          navigation={navigation}
        />
      )}

      {/* FINAL STEP: Review & Submit */}
      {currentStep === totalSteps - 1 && (
        <ReviewStep
          sections={sections}
          answers={formState.answers}
          vessel={vessel}
          inspectionType={inspectionType}
          passCount={passCount}
          failCount={failCount}
          naCount={naCount}
          totalAnswered={totalAnswered}
          totalQuestions={totalQuestions}
          notes={formState.inspectorNotes}
          onNotesChange={(v) => setFormState(s => ({ ...s, inspectorNotes: v }))}
          onOpenSignature={() => setShowSignaturePad(true)}
          signatureData={formState.signatureData}
          navigation={navigation}
        />
      )}

      {/* Photo Modal */}
      {showPhotoModal && activeQuestionForPhoto && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPhotoModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800">Add Photo Evidence</h3>
              <button onClick={() => setShowPhotoModal(false)} className="p-1 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-sky-300 transition-colors cursor-pointer">
              <Camera className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-600">Tap to take photo or upload</p>
              <p className="text-xs text-slate-400 mt-1">Supports JPG, PNG, HEIC</p>
            </div>
            <div className="mt-4 flex gap-2">
              <button className="flex-1 px-4 py-2.5 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 transition-colors flex items-center justify-center gap-2">
                <Camera className="w-4 h-4" /> Take Photo
              </button>
              <button className="flex-1 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2">
                <Upload className="w-4 h-4" /> Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowNotesFor(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800">Add Notes</h3>
              <button onClick={() => setShowNotesFor(null)} className="p-1 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <textarea
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
              rows={4}
              placeholder="Describe the observation..."
              defaultValue={formState.answers[showNotesFor]?.notes || ''}
              onChange={(e) => {
                const val = e.target.value;
                setObservation(showNotesFor, 'notes', val);
              }}
            />
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Severity</label>
                <select
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  value={formState.answers[showNotesFor]?.severity || ''}
                  onChange={(e) => setObservation(showNotesFor, 'severity', e.target.value || undefined)}
                >
                  <option value="">None</option>
                  <option value="critical">Critical</option>
                  <option value="major">Major</option>
                  <option value="minor">Minor</option>
                  <option value="observation">Observation</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Risk Level</label>
                <select
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  value={formState.answers[showNotesFor]?.riskLevel || ''}
                  onChange={(e) => setObservation(showNotesFor, 'riskLevel', e.target.value || undefined)}
                >
                  <option value="">None</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
            <button
              onClick={() => setShowNotesFor(null)}
              className="mt-4 w-full px-4 py-2.5 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 transition-colors"
            >
              Save Notes
            </button>
          </div>
        </div>
      )}

      {/* Signature Modal */}
      {showSignaturePad && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowSignaturePad(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800">Inspector Signature</h3>
              <button onClick={() => setShowSignaturePad(false)} className="p-1 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="border-2 border-slate-200 rounded-xl p-6 text-center">
              <Pen className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Draw your signature below</p>
              <div className="mt-3 h-32 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-center">
                <p className="text-xs text-slate-400">Signature pad area</p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => { setFormState(s => ({ ...s, signatureData: 'signed' })); setShowSignaturePad(false); }}
                className="flex-1 px-4 py-2.5 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 transition-colors"
              >
                Confirm Signature
              </button>
              <button
                onClick={() => setShowSignaturePad(false)}
                className="px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========================
// SECTION STEP COMPONENT
// ========================
function SectionStep({
  section, answers, sectionIndex, totalSections,
  onSetAnswer, onSetObservation, onOpenNotes, onOpenPhoto,
  notes, onNotesChange, navigation
}: {
  section: QuestionnaireSection;
  answers: Record<string, Answer>;
  sectionIndex: number;
  totalSections: number;
  onSetAnswer: (qId: string, result: AnswerResult) => void;
  onSetObservation: (qId: string, field: string, value: any) => void;
  onOpenNotes: (qId: string) => void;
  onOpenPhoto: (qId: string) => void;
  notes: string;
  onNotesChange: (v: string) => void;
  navigation: React.ReactNode;
}) {
  const answered = section.questions.filter(q => answers[q.id]?.result && answers[q.id]?.result !== 'pending').length;
  const passCount = section.questions.filter(q => answers[q.id]?.result === 'pass').length;
  const failCount = section.questions.filter(q => answers[q.id]?.result === 'fail').length;

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 bg-sky-100 text-sky-700 rounded-lg flex items-center justify-center text-xs font-bold">{sectionIndex + 1}</span>
              <h2 className="text-lg font-semibold text-slate-800">{section.title}</h2>
            </div>
            <p className="text-sm text-slate-500 ml-9">{section.description}</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-slate-800">{answered}/{section.questions.length}</p>
            <p className="text-[10px] text-slate-400">answered</p>
          </div>
        </div>
        <div className="flex gap-4 mt-3 ml-9 text-xs">
          <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-emerald-500" /> {passCount} Pass</span>
          <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-500" /> {failCount} Fail</span>
          <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-slate-300" /> {section.questions.length - answered} Pending</span>
        </div>
      </div>

      {/* Questions */}
      {section.questions.map((question, qIdx) => {
        const answer = answers[question.id];
        const result = answer?.result || 'pending';
        const hasFail = result === 'fail';

        return (
          <div key={question.id} className={`bg-white rounded-2xl border p-5 transition-all ${
            hasFail ? 'border-red-200 shadow-sm shadow-red-50' : 'border-slate-100'
          }`}>
            <div className="flex items-start gap-4">
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                result === 'pass' ? 'bg-emerald-100 text-emerald-700' :
                result === 'fail' ? 'bg-red-100 text-red-700' :
                result === 'na' ? 'bg-slate-100 text-slate-500' :
                'bg-sky-50 text-sky-500'
              }`}>
                {qIdx + 1}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2">
                  <p className="text-sm font-medium text-slate-800 leading-relaxed">{question.text}</p>
                  {question.isRequired && <span className="text-red-400 text-xs mt-0.5">*</span>}
                </div>
                {question.guidance && (
                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                    <Info className="w-3 h-3" /> {question.guidance}
                  </p>
                )}
                {question.isRiskWeighted && (
                  <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-medium rounded-full">
                    <AlertCircle className="w-3 h-3" /> Risk Weight: {question.riskWeight}
                  </span>
                )}

                {/* Answer Buttons */}
                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={() => onSetAnswer(question.id, 'pass')}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      result === 'pass'
                        ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20'
                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                    }`}
                  >
                    <CheckCircle2 className="w-4 h-4" /> Pass
                  </button>
                  <button
                    onClick={() => onSetAnswer(question.id, 'fail')}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      result === 'fail'
                        ? 'bg-red-500 text-white shadow-md shadow-red-500/20'
                        : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                    }`}
                  >
                    <XCircle className="w-4 h-4" /> Fail
                  </button>
                  <button
                    onClick={() => onSetAnswer(question.id, 'na')}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                      result === 'na'
                        ? 'bg-slate-500 text-white shadow-md shadow-slate-500/20'
                        : 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200'
                    }`}
                  >
                    <Minus className="w-4 h-4" /> N/A
                  </button>
                </div>

                {/* Notes, Photo, GPS buttons - always visible */}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                  <button
                    onClick={() => onOpenNotes(question.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      answer?.notes
                        ? 'bg-amber-100 text-amber-800 border border-amber-300'
                        : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                    }`}
                  >
                    <StickyNote className="w-3.5 h-3.5" /> Notes
                    {answer?.notes && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
                  </button>
                  <button
                    onClick={() => onOpenPhoto(question.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      answer?.photos && answer.photos.length > 0
                        ? 'bg-blue-100 text-blue-800 border border-blue-300'
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                    }`}
                  >
                    <Camera className="w-3.5 h-3.5" /> Photo
                    {answer?.photos && answer.photos.length > 0 && (
                      <span className="bg-blue-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">{answer.photos.length}</span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (navigator.geolocation) {
                        navigator.geolocation.getCurrentPosition(
                          (position) => {
                            onSetObservation(question.id, 'gpsLocation', {
                              lat: position.coords.latitude,
                              lng: position.coords.longitude,
                            });
                          },
                          () => {},
                          { enableHighAccuracy: true, timeout: 10000 }
                        );
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      answer?.gpsLocation
                        ? 'bg-purple-100 text-purple-800 border border-purple-300'
                        : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200'
                    }`}
                  >
                    <MapPin className="w-3.5 h-3.5" /> GPS
                    {answer?.gpsLocation && <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />}
                  </button>
                </div>

                {/* Notes preview */}
                {answer?.notes && (
                  <div className="mt-2 p-2 bg-slate-50 rounded-lg text-xs text-slate-600">
                    <StickyNote className="w-3 h-3 inline mr-1" />
                    {answer.notes.substring(0, 100)}{answer.notes.length > 100 ? '...' : ''}
                  </div>
                )}

                {/* GPS Location preview */}
                {answer?.gpsLocation && (
                  <div className="mt-2 p-2 bg-purple-50 rounded-lg text-xs text-purple-700 flex items-center gap-2">
                    <MapPin className="w-3 h-3" />
                    <span>Lat: {answer.gpsLocation.lat.toFixed(6)}, Lng: {answer.gpsLocation.lng.toFixed(6)}</span>
                    <a
                      href={`https://www.google.com/maps?q=${answer.gpsLocation.lat},${answer.gpsLocation.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto text-purple-600 hover:text-purple-800 underline"
                    >
                      View on map
                    </a>
                  </div>
                )}

                {/* Photo thumbnails preview */}
                {answer?.photos && answer.photos.length > 0 && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex -space-x-1.5">
                      {answer.photos.slice(0, 4).map((photo, i) => (
                        <div key={i} className="w-10 h-10 rounded-lg border-2 border-white overflow-hidden bg-slate-100 shadow-sm">
                          <img src={photo} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                    {answer.photos.length > 4 && (
                      <span className="text-xs text-slate-500 font-medium">+{answer.photos.length - 4} more</span>
                    )}
                  </div>
                )}

                {/* Finding, Corrective Action & Responsible Fields */}
                {(result === 'fail' || result === 'pass') && (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-2.5">
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Finding</label>
                      <textarea
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                        rows={2}
                        placeholder="Describe the finding..."
                        value={answer?.finding || ''}
                        onChange={(e) => onSetObservation(question.id, 'finding', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Corrective / Preventive Action</label>
                      <textarea
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                        rows={2}
                        placeholder="Recommended corrective or preventive action..."
                        value={answer?.correctiveAction || ''}
                        onChange={(e) => onSetObservation(question.id, 'correctiveAction', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Responsible</label>
                      <select
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                        value={answer?.responsible || ''}
                        onChange={(e) => onSetObservation(question.id, 'responsible', e.target.value)}
                      >
                        <option value="">Select...</option>
                        <option value="Vessel">Vessel</option>
                        <option value="TSI">TSI</option>
                        <option value="TSI / Vessel">TSI / Vessel</option>
                        <option value="Q&S Supdt">Q&S Supdt</option>
                        <option value="Q&S / Vessel">Q&S / Vessel</option>
                        <option value="Crewing">Crewing</option>
                        <option value="Operations">Operations</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Section Notes */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <label className="block text-sm font-medium text-slate-600 mb-2">Section Notes (Optional)</label>
        <textarea
          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
          rows={3}
          placeholder="Add any additional notes for this section..."
          value={notes}
          onChange={e => onNotesChange(e.target.value)}
        />
      </div>

      {navigation}
    </div>
  );
}

// ========================
// REVIEW STEP COMPONENT
// ========================
function ReviewStep({
  sections, answers, vessel, inspectionType,
  passCount, failCount, naCount, totalAnswered, totalQuestions,
  notes, onNotesChange, onOpenSignature, signatureData, navigation
}: {
  sections: QuestionnaireSection[];
  answers: Record<string, Answer>;
  vessel: any;
  inspectionType: string;
  passCount: number;
  failCount: number;
  naCount: number;
  totalAnswered: number;
  totalQuestions: number;
  notes: string;
  onNotesChange: (v: string) => void;
  onOpenSignature: () => void;
  signatureData: string | null;
  navigation: React.ReactNode;
}) {
  const unansweredCount = totalQuestions - totalAnswered;
  const completionPct = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;
  const score = totalAnswered > 0 ? ((passCount / totalAnswered) * 10).toFixed(1) : '0.0';

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-1">Inspection Summary</h2>
        <p className="text-sm text-slate-500">Review all findings before submitting</p>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5">
          {[
            { label: 'Completion', value: `${completionPct}%`, color: 'bg-sky-50 text-sky-700' },
            { label: 'Passed', value: passCount, color: 'bg-emerald-50 text-emerald-700' },
            { label: 'Failed', value: failCount, color: 'bg-red-50 text-red-700' },
            { label: 'N/A', value: naCount, color: 'bg-slate-50 text-slate-600' },
            { label: 'Score', value: `${score}/10`, color: 'bg-amber-50 text-amber-700' },
          ].map(card => (
            <div key={card.label} className={`rounded-xl p-3 text-center ${card.color}`}>
              <p className="text-2xl font-bold">{card.value}</p>
              <p className="text-[10px] font-medium opacity-70">{card.label}</p>
            </div>
          ))}
        </div>

        {/* Missing Answers Warning */}
        {unansweredCount > 0 && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">{unansweredCount} questions unanswered</p>
              <p className="text-xs text-amber-600 mt-0.5">You can still submit, but unanswered questions will be marked as "Pending".</p>
            </div>
          </div>
        )}

        {/* Section Breakdown */}
        <div className="mt-5 space-y-2">
          {sections.map((section, i) => {
            const sectPass = section.questions.filter(q => answers[q.id]?.result === 'pass').length;
            const sectFail = section.questions.filter(q => answers[q.id]?.result === 'fail').length;
            const sectTotal = section.questions.length;
            const sectAnswered = sectPass + sectFail + section.questions.filter(q => answers[q.id]?.result === 'na').length;
            const pct = sectTotal > 0 ? Math.round((sectAnswered / sectTotal) * 100) : 0;

            return (
              <div key={section.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <span className="w-6 h-6 bg-slate-200 rounded-lg flex items-center justify-center text-[10px] font-bold text-slate-600">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{section.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-sky-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[10px] text-slate-400 w-12 text-right">{sectAnswered}/{sectTotal}</span>
                  </div>
                </div>
                {sectFail > 0 && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full">{sectFail} fail</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Failed Findings Summary */}
        {failCount > 0 && (
          <div className="mt-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-2">Failed Findings</h3>
            <div className="space-y-2">
              {sections.flatMap(s => s.questions)
                .filter(q => answers[q.id]?.result === 'fail')
                .map(q => (
                  <div key={q.id} className="p-3 bg-red-50 border border-red-100 rounded-xl">
                    <div className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-red-800">{q.text}</p>
                        {answers[q.id]?.notes && (
                          <p className="text-[10px] text-red-600 mt-1">Note: {answers[q.id].notes}</p>
                        )}
                        {answers[q.id]?.finding && (
                          <p className="text-[10px] text-red-600 mt-1">Finding: {answers[q.id].finding}</p>
                        )}
                        {answers[q.id]?.correctiveAction && (
                          <p className="text-[10px] text-red-600 mt-1">Action: {answers[q.id].correctiveAction}</p>
                        )}
                        {answers[q.id]?.responsible && (
                          <p className="text-[10px] text-red-600 mt-1">Responsible: {answers[q.id].responsible}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Inspector Notes */}
        <div className="mt-5">
          <label className="block text-sm font-medium text-slate-600 mb-2">Executive Summary</label>
          <textarea
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
            rows={4}
            placeholder="Provide an executive summary of the inspection findings..."
            value={notes}
            onChange={e => onNotesChange(e.target.value)}
          />
        </div>

        {/* Signature */}
        <div className="mt-5">
          <label className="block text-sm font-medium text-slate-600 mb-2">Inspector Signature</label>
          {signatureData ? (
            <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              <span className="text-sm font-medium text-emerald-700">Signature confirmed</span>
              <button onClick={onOpenSignature} className="ml-auto text-xs text-emerald-600 hover:underline">Re-sign</button>
            </div>
          ) : (
            <button
              onClick={onOpenSignature}
              className="w-full p-4 border-2 border-dashed border-slate-200 rounded-xl text-center hover:border-sky-300 transition-colors"
            >
              <Pen className="w-6 h-6 text-slate-300 mx-auto mb-1" />
              <p className="text-sm text-slate-500">Tap to sign</p>
            </button>
          )}
        </div>
      </div>

      {navigation}
    </div>
  );
}
